// Guided tours for the /users page.
//
// USERS_OVERVIEW_TOUR_ID
//   Auto-runs once per viewer on the first visit to /users. Every step targets
//   an element that always renders for any viewer allowed onto /users
//   (VIEW_PARTNER_DETAILS + requiresPartner), so no per-permission gating is
//   needed inside the steps. No FAQ outro: by the time a viewer reaches /users
//   they have already seen the outro at the end of their first-login tour
//   (AdminDashboard → PartnerDetails setup for admins, or MyWalletDashboard →
//   MyActivity for wallet holders).
//
// USERS_ADD_USER_TOUR_ID
//   Replay-only. Enabled by Users.js when the URL carries ?tour=add-user AND
//   the viewer has MANAGE_PARTNER_USERS (the same guard that shows the Add
//   User button and the modal). The tour opens the Add User modal from its
//   first step and walks each field. If the modal cannot be opened the tour
//   simply does not run.

export const USERS_OVERVIEW_TOUR_ID = 'users-overview-v1';
export const USERS_ADD_USER_TOUR_ID = 'users-add-user-v1';

const visibleElementFor = (selector) => () => {
  const candidates = document.querySelectorAll(selector);
  for (const candidate of candidates) {
    if (candidate.offsetParent !== null) return candidate;
  }
  return null;
};

export const buildUsersOverviewTourSteps = () => [
  {
    element: visibleElementFor('[data-tour="users-card"]'),
    title: 'Your people, at a glance',
    description: 'Everyone on your Milichain team lives here. Wallet holders, admins, reviewers, all in one list, so you can find and act on the right person quickly.',
    side: 'bottom',
    align: 'start',
  },
  {
    element: visibleElementFor('[data-tour="users-search"]'),
    title: 'Looking for someone?',
    description: 'Start typing a name, email, or phone number. The list narrows as you type, so you never have to scroll a long roster.',
    side: 'bottom',
    align: 'start',
  },
  {
    element: visibleElementFor('[data-tour="users-role-filter"]'),
    title: 'Focus on a role',
    description: 'Trim the list to just Super Admins, Accountants, or regular Users. Combine it with search when you know roughly who you are looking for.',
    side: 'bottom',
    align: 'start',
  },
  {
    element: visibleElementFor('[data-tour="users-status-filter"]'),
    title: 'Focus on a status',
    description: 'Switch to Active, Pending, Inactive, or Suspended to see just the people in that state. Suspended brings up a link to the full suspensions log so you can see reasons, dates, and who set them.',
    side: 'bottom',
    align: 'start',
  },
  {
    element: visibleElementFor('[data-tour="users-row-actions"]'),
    title: 'Do something to a person',
    description: 'Tap the three-dot button on any row to open a panel of actions for that person: view more details, edit status, manage their transaction limits, activate their wallet, assign them to a team, or delete. You only see the actions you have permission for and that make sense for their current state.',
    side: 'left',
    align: 'start',
    // Only rendered when at least one partner user row exists; skipped
    // silently on an empty list so the tour still fires end to end.
    skipMissingElement: true,
  },
];

export const buildUsersAddUserTourSteps = () => [
  {
    element: visibleElementFor('[data-tour="users-add-name-fields"]'),
    title: 'Start with their name',
    description: 'First and last are what everyone in Milichain will see. If you know the name they actually go by, drop it into Preferred, for example "Ali" if their first name is Alistair. It takes two seconds and makes every greeting sound a little more like them. Skip it if you honestly do not know; we will fall back to first name for you.',
    side: 'right',
    align: 'start',
  },
  {
    element: visibleElementFor('[data-tour="users-add-contact-fields"]'),
    title: 'How you will reach them',
    description: 'Type their email. That is where the sign-in invite lands and every Milichain notification after that. A phone is optional; add one if you have it, and as you type we tidy it into the clean 0712345678 shape so spaces, dashes, and stray characters do not matter.',
    side: 'right',
    align: 'start',
  },
  {
    element: visibleElementFor('[data-tour="users-add-role"]'),
    title: 'Pick their role',
    description: 'This is what decides what they can see and do inside Milichain. Super Admin runs the whole organisation. Accountant and User are day-to-day roles with narrower scope on payments and approvals.',
    side: 'right',
    align: 'start',
  },
  {
    element: visibleElementFor('[data-tour="users-add-wallet-toggle"]'),
    title: 'Give them a wallet?',
    description: 'Flip to Yes if this person should be able to send money, pay bills, or receive disbursements. A team question appears once you do, because every wallet holder belongs to a team that reviews their larger payments. Leave it on No if this person is admin-only; you can always activate a wallet later from the row actions.',
    side: 'right',
    align: 'start',
  },
  {
    element: visibleElementFor('[data-tour="users-add-submit"]'),
    title: 'You are done',
    description: 'Add lights up once every required field is filled. When you submit, we create the account and email them the sign-in invite.',
    side: 'top',
    align: 'start',
  },
];
